import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Users, ShieldCheck } from 'lucide-react'
import {
  DEVICE_CHECK_MS,
  MEMBER_SESSION_RENEW_MS,
  SHARED_DEVICE_IDLE_MS,
  SHARED_EXIT_EVENT,
  endParentWindow,
  registerSessionResetHandler,
  checkDeviceCredential,
  endSharedSession,
  getDeviceCredential,
  getSharedSession,
  startMemberSession,
  subscribeSharedSession,
} from '@/utils/sharedDevice'
import { StepUpSheet } from './StepUpSheet'

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const

/**
 * Always mounted. While a shared-device member session is active it shows
 * the "Shared device: <name>" bar, returns to the picker after
 * SHARED_DEVICE_IDLE_MS without input, renews member sessions, runs the
 * parent window countdown, and hosts the step-up sheet.
 */
export const SharedDeviceController: React.FC = () => {
  const session = useSyncExternalStore(subscribeSharedSession, getSharedSession)
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [now, setNow] = useState(() => Date.now())
  const lastActivity = useRef(Date.now())
  const renewing = useRef(false)

  // Every change of person or parent window starts from an empty query
  // cache, so cached 403s and the previous person's data never show.
  useEffect(() => {
    registerSessionResetHandler(() => queryClient.clear())
    return () => registerSessionResetHandler(null)
  }, [queryClient])

  // Any shared session that ends (401, revoke, window closed with no
  // fallback) returns to the picker.
  useEffect(() => {
    const onExit = () => navigate('/device', { replace: true })
    window.addEventListener(SHARED_EXIT_EVENT, onExit)
    return () => window.removeEventListener(SHARED_EXIT_EVENT, onExit)
  }, [navigate])

  // While this browser is a paired device, check the credential every
  // minute (and when the screen becomes visible again), so a device revoked
  // in Settings > Devices returns to the pairing screen even if nobody is
  // using it, including from inside a member session.
  useEffect(() => {
    let stopped = false
    const check = async () => {
      if (stopped || document.hidden || !getDeviceCredential()) return
      if ((await checkDeviceCredential()) === 'unpaired' && !stopped) {
        navigate('/device', { replace: true })
      }
    }
    const timer = setInterval(check, DEVICE_CHECK_MS)
    const onVisible = () => {
      if (!document.hidden) check()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopped = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [navigate])

  const active = !!session

  // A member who signs out from the account menu lands on /login; on a
  // shared device that means "done", so go back to the picker.
  const wasActive = useRef(false)
  useEffect(() => {
    if (session) {
      wasActive.current = true
    } else if (wasActive.current && location.pathname === '/login') {
      wasActive.current = false
      navigate('/device', { replace: true })
    }
  }, [session, location.pathname, navigate])

  // Activity tracking and the 1-second clock, only while a session is active.
  useEffect(() => {
    if (!active) return
    lastActivity.current = Date.now()
    const mark = () => {
      lastActivity.current = Date.now()
    }
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, mark, { passive: true, capture: true }))
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, mark, { capture: true }))
      clearInterval(t)
    }
  }, [active])

  // Idle return, member session renewal, and the end of a parent window.
  useEffect(() => {
    if (!session) return
    if (now - lastActivity.current >= SHARED_DEVICE_IDLE_MS) {
      endSharedSession()
      navigate('/device', { replace: true })
      return
    }
    if (renewing.current) return
    if (session.parentWindowEndsAt) {
      if (now >= session.parentWindowEndsAt) {
        // Back to the picker, not to a parent session without rights.
        endParentWindow()
      }
    } else if (now - session.startedAt >= MEMBER_SESSION_RENEW_MS || now >= session.expiresAt - 60_000) {
      renewing.current = true
      startMemberSession(session.member)
        .catch(() => {
          endSharedSession()
          navigate('/device', { replace: true })
        })
        .finally(() => {
          renewing.current = false
        })
    }
  }, [now, session, navigate])

  const show = active && location.pathname !== '/device'

  const switchPerson = () => {
    endSharedSession()
    navigate('/device', { replace: true })
  }

  const windowLeft = session?.parentWindowEndsAt
    ? Math.max(0, Math.round((session.parentWindowEndsAt - now) / 1000))
    : null

  return (
    <>
      {show && session && (
        <>
          <div aria-hidden="true" className="h-20" />
          <div
            role="region"
            aria-label="Shared device"
            className="fixed bottom-0 inset-x-0 z-[60] bg-indigo-950 text-white shadow-[0_-4px_16px_rgba(0,0,0,0.25)]"
          >
            {windowLeft !== null && (
              <div className="bg-emerald-600 px-4 py-2 text-base sm:text-lg font-semibold flex items-center justify-center gap-2" role="status">
                <ShieldCheck className="w-5 h-5" aria-hidden="true" />
                Parent approval active: {Math.floor(windowLeft / 60)}:{String(windowLeft % 60).padStart(2, '0')} left
              </div>
            )}
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
              <p className="text-lg sm:text-xl flex items-center gap-3 min-w-0">
                <Users className="w-6 h-6 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">
                  Shared device: <strong>{session.member.display_name}</strong>
                </span>
              </p>
              <button
                type="button"
                onClick={switchPerson}
                className="min-h-[56px] px-6 rounded-xl bg-white text-indigo-950 text-lg font-semibold hover:bg-indigo-100
                  focus:outline-none focus:ring-4 focus:ring-yellow-300 flex-shrink-0"
              >
                Switch person
              </button>
            </div>
          </div>
        </>
      )}
      {(active || location.pathname === '/device') && <StepUpSheet />}
    </>
  )
}

export default SharedDeviceController
